import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const LanguageContext = createContext(null);

export const dict = {
  de: {
    nav: { tenders: "Ausschreibungen", partners: "Partner", workspace: "Workspace", earlyAccess: "Early Access" },
    cta: { primary: "Early Access sichern", secondary: "Produkt ansehen" },
    hero: {
      badge: "Pre-Launch · KI-gestützt",
      line1: "Ausschreibungen finden.",
      line2: "Partner matchen.",
      line3: "Gemeinsam gewinnen.",
      subline: "Die Plattform für öffentliche Vergabe: relevante Ausschreibungen, die richtigen Konsortialpartner und ein gemeinsamer Workspace.",
      chips: ["Vergabeportale", "KI-Matching", "Konsortien", "Audit-ready"],
    },
    waitlistPrefix: "Auf der Warteliste",
    tender: {
      eyebrow: "Tender Matching",
      title: "Mehrere Vergabeportale.",
      accent: "Ein Feed.",
      subline: "Alle öffentlichen Ausschreibungen aus verschiedenen Portalen – zentral, gefiltert und KI-bewertet.",
      features: [
        { l: "Alle Quellen" }, { l: "Intelligenter Filter" }, { l: "KI-gestütztes Matching" },
        { l: "Relevante Treffer" }, { l: "Mehr Chancen gewinnen" },
      ],
    },
    partner: {
      eyebrow: "AI Business Partner Matching",
      title: "Die richtigen Partner.",
      accent: "Mehr gewinnen.",
      subline: "Die KI analysiert 100+ Attribute und findet die kompatibelsten Konsortialpartner für Ihr Projekt.",
      pipeline: [
        { t: "Company Data", s: "Multi-source" },
        { t: "AI Matching Engine", s: "Skills · Erfahrung · Kapazität" },
        { t: "Compatibility Scoring", s: "100+ Attribute" },
        { t: "Best Partner Match", s: "Für Ihr Projekt" },
      ],
    },
    workspace: {
      eyebrow: "Consortium Workspace",
      title: "Zusammenarbeiten. Gemeinsam erstellen.",
      accent: "Gewinnen.",
      subline: "Ein gemeinsamer Workspace für Konsortien, um Angebote gemeinsam zu erstellen und zu gewinnen.",
      features: [
        { l: "Echtzeit-Zusammenarbeit", s: "Alle am gleichen Dokument" },
        { l: "Klare Aufgabenverteilung", s: "Jeder weiß, was zu tun ist" },
        { l: "Versionskontrolle", s: "Änderungen nachvollziehbar" },
        { l: "Sichere Daten", s: "DSGVO-konform & verschlüsselt" },
      ],
    },
    audience: {
      eyebrow: "Zielgruppen",
      title: "Für Teams, die Vergabe ernst nehmen.",
      chips: ["KMU", "IT-Dienstleister", "Planung & Ingenieurwesen", "Energie & Bau", "Beratung", "Agenturen"],
    },
    problem: {
      eyebrow: "Das Problem",
      title: "Die richtige Ausschreibung.",
      accent: "Das falsche Team.",
      lead: "Sie finden die perfekte Ausschreibung — können sie aber allein nicht umsetzen.",
      gapsLabel: "Was oft fehlt",
      gaps: ["Expertise", "Zertifizierung", "Referenzprojekt", "Lokale Präsenz", "Lieferkapazität"],
      chaosLabel: "Partnersuche läuft heute über",
      chaos: ["Netzwerk", "E-Mails", "Anrufe", "Tabellen", "PDFs"],
      highlight: "Für KMU heißt das: verlorene Zeit und verpasste Chancen — nur weil der richtige Partner nicht rechtzeitig da war.",
    },
    solution: {
      eyebrow: "Die Lösung",
      title: "Das richtige Konsortium",
      accent: "für jede Ausschreibung.",
      subline: "Procurmo analysiert die Anforderungen, erkennt Ihre Lücken und verbindet Sie mit komplementären Partnern — damit Sie stärkere Konsortialangebote schneller aufbauen, steuern und einreichen.",
      intro2: "Von dort hilft Procurmo, komplementäre Unternehmen zu finden, zu bewerten und zu verbinden, das Konsortium aufzubauen und das Angebot in einem gemeinsamen Workspace zu koordinieren.",
      stepsTitle: "So funktioniert es",
      steps: [
        { t: "Ausschreibung verstehen", d: "Die KI extrahiert zentrale Anforderungen, Eignungskriterien, Expertise, Referenzen und Zertifizierungen." },
        { t: "Ihre Lücken erkennen", d: "Procurmo vergleicht die Anforderungen mit Ihrem Unternehmensprofil und zeigt, was fehlt." },
        { t: "Die richtigen Partner finden", d: "Entdecken Sie Unternehmen, die Ihre Fähigkeiten ergänzen und zur konkreten Ausschreibung passen." },
        { t: "Konsortium aufbauen", d: "Definieren Sie Rollen, Verantwortlichkeiten, Arbeitspakete und Partnerbeiträge in einem Workspace." },
        { t: "Gemeinsam bieten", d: "Koordinieren Sie Dokumente, Compliance-Anforderungen, Fristen und Angebotsbeiträge über das gesamte Konsortium." },
      ],
    },
    featuresOverview: {
      eyebrow: "Funktionen",
      title: "Drei Module.",
      accent: "Ein Workflow.",
      items: [
        { t: "Ausschreibungs-Matching", d: "Passende öffentliche Ausschreibungen automatisch finden und nach Relevanz bewerten.", href: "#tenders" },
        { t: "KI-Geschäftspartner-Matching", d: "Komplementäre Unternehmen finden, die genau die geforderten Kriterien erfüllen.", href: "#partners" },
        { t: "Konsortial-Workspace", d: "Rollen, Dokumente, Fristen und das Angebot gemeinsam an einem Ort steuern.", href: "#workspace" },
      ],
      more: "Mehr erfahren",
    },
    benefits: {
      eyebrow: "Vorteile",
      title: "Warum Procurmo",
      accent: "den Unterschied macht.",
      items: [
        { t: "Chancen nutzen, die allein unerreichbar waren", d: "Erschließen Sie größere und komplexere Ausschreibungen, indem Sie komplementäre Fähigkeiten bündeln." },
        { t: "Partner nach Anforderungen finden — nicht nur im Netzwerk", d: "Weg von „Kenne ich jemanden?“ hin zu strukturiertem Partner-Matching." },
        { t: "Konsortien schneller aufbauen", d: "Weniger Zeit für Suche, Kontaktaufnahme und Bewertung potenzieller Partner." },
        { t: "Alle bleiben abgestimmt", d: "Ein Workspace für Verantwortlichkeiten, Dokumente, Anforderungen, Fristen und Beiträge." },
        { t: "Konsortialrisiko reduzieren", d: "Fehlende Qualifikationen, unklare Verantwortlichkeiten und Compliance-Lücken vor der Abgabe erkennen." },
      ],
    },
    differentiation: {
      eyebrow: "Differenzierung",
      title: "Andere Tools finden Ausschreibungen oder schreiben Angebote.",
      accent: "Procurmo baut das Team, das sie umsetzt.",
      rows: [
        { k: "Vergabeplattformen", v: "organisieren Chancen." },
        { k: "KI-Angebotstools", v: "organisieren Inhalte." },
        { k: "Procurmo", v: "organisiert Unternehmen, Fähigkeiten und Zusammenarbeit." },
      ],
      pillars: ["Tender Intelligence", "Partner-Matching", "Konsortial-Management", "Kollaboratives Bieten"],
      combine: "Wir vereinen Tender Intelligence, Partner-Matching, Konsortial-Management und kollaboratives Bieten in einem Workflow.",
      closer: "Denn in der komplexen öffentlichen Vergabe beginnt das stärkste Angebot oft mit dem richtigen Konsortium.",
    },
    form: {
      eyebrow: "Early Access",
      title: "Früh dabei sein.",
      subline: "Jetzt registrieren! Wir melden uns, wenn wir so weit sind.",
      name: "Name", namePh: "Vor- und Nachname",
      email: "E-Mail", emailPh: "name@firma.de",
      company: "Firma", companyPh: "Ihr Unternehmen",
      companyType: "Unternehmenstyp", companyTypePh: "Auswählen",
      q1: "Wie oft bieten Sie auf öffentliche Ausschreibungen?", q1Ph: "Auswählen",
      q2: "Wo ist heute der Engpass?", q2Ph: "Auswählen",
      submit: "Auf Warteliste setzen", submitting: "Wird gesendet …",
      reassurance: "Kein Spam. Abmeldung jederzeit.",
      successTitle: "Sie stehen auf der Liste.",
      successBody: "Danke — wir melden uns, wenn wir so weit sind.",
      again: "Weitere Anmeldung",
      errRequired: "Bitte Pflichtfelder ausfüllen.",
      errEmail: "Bitte gültige E-Mail eingeben.",
      errDuplicate: "Diese E-Mail ist bereits auf der Warteliste.",
      errGeneric: "Etwas ist schiefgelaufen. Bitte erneut versuchen.",
    },
    companyTypes: {
      sme: "KMU", consulting: "Beratung", it_services: "IT-Dienstleister",
      engineering: "Planung & Ingenieurwesen", energy_construction: "Energie & Bau",
      agency: "Agentur", public_contractor: "Öffentlicher Auftragnehmer", other: "Sonstige",
    },
    frequencies: { monthly: "Monatlich", quarterly: "Quartalsweise", rarely: "Selten", new: "Neu dabei" },
    bottlenecks: { tenders: "Ausschreibungen finden", partners: "Partner finden", coordination: "Koordination", documents: "Dokumente & Versionen" },
    footer: {
      tagline: "Ausschreibungen, Partner und Angebote — in einer Plattform.",
      product: "Produkt", legal: "Rechtliches",
      imprint: "Impressum", privacy: "Datenschutz", contact: "Kontakt",
      prelaunch: "Pre-Launch", rights: "Alle Rechte vorbehalten.",
    },
  },

  en: {
    nav: { tenders: "Tenders", partners: "Partners", workspace: "Workspace", earlyAccess: "Early Access" },
    cta: { primary: "Request Early Access", secondary: "See product" },
    hero: {
      badge: "Pre-launch · AI-powered",
      line1: "Find tenders.",
      line2: "Match partners.",
      line3: "Win together.",
      subline: "The platform for public procurement: relevant tenders, the right consortium partners and one shared workspace.",
      chips: ["Procurement portals", "AI matching", "Consortia", "Audit-ready"],
    },
    waitlistPrefix: "On the waitlist",
    tender: {
      eyebrow: "Tender Matching",
      title: "Multiple procurement portals.",
      accent: "One feed.",
      subline: "All public tenders from many portals — centralized, filtered and AI-scored.",
      features: [
        { l: "All sources" }, { l: "Smart filter" }, { l: "AI matching" },
        { l: "Relevant hits" }, { l: "Win more" },
      ],
    },
    partner: {
      eyebrow: "AI Business Partner Matching",
      title: "The right partners.",
      accent: "Win more.",
      subline: "The AI analyzes 100+ attributes to find the most compatible consortium partners for your project.",
      pipeline: [
        { t: "Company Data", s: "Multi-source" },
        { t: "AI Matching Engine", s: "Skills · experience · capacity" },
        { t: "Compatibility Scoring", s: "100+ attributes" },
        { t: "Best Partner Match", s: "For your project" },
      ],
    },
    workspace: {
      eyebrow: "Consortium Workspace",
      title: "Collaborate. Co-create.",
      accent: "Win.",
      subline: "A shared workspace for consortium members to build winning proposals together.",
      features: [
        { l: "Real-time collaboration", s: "Everyone on the same doc" },
        { l: "Clear task ownership", s: "Everyone knows what to do" },
        { l: "Version control", s: "Every change traceable" },
        { l: "Secure data", s: "GDPR-ready & encrypted" },
      ],
    },
    audience: {
      eyebrow: "Audience",
      title: "For teams serious about procurement.",
      chips: ["SMEs", "IT services", "Engineering & planning", "Energy & construction", "Consulting", "Agencies"],
    },
    problem: {
      eyebrow: "The Problem",
      title: "The right tender.",
      accent: "The wrong team.",
      lead: "You find the perfect tender — but you can't deliver it alone.",
      gapsLabel: "What's often missing",
      gaps: ["Expertise", "Certification", "Reference project", "Local presence", "Delivery capacity"],
      chaosLabel: "Partner search runs on",
      chaos: ["Network", "Emails", "Calls", "Spreadsheets", "PDFs"],
      highlight: "For SMEs, that means lost time and missed opportunities — simply because the right partner wasn't there in time.",
    },
    solution: {
      eyebrow: "The Solution",
      title: "The right consortium",
      accent: "for every tender.",
      subline: "Procurmo analyses the requirements, identifies your gaps and connects you with complementary partners — so you can build, manage and submit stronger consortium bids faster.",
      intro2: "From there, Procurmo helps you find, assess and connect with complementary companies, build the consortium and coordinate the bid in one shared workspace.",
      stepsTitle: "How it works",
      steps: [
        { t: "Understand the tender", d: "AI extracts key requirements, eligibility criteria, expertise, references and certifications." },
        { t: "Identify your gaps", d: "Procurmo compares the tender requirements with your company profile and shows what is missing." },
        { t: "Find the right partners", d: "Discover companies that complement your capabilities and match the specific tender requirements." },
        { t: "Build the consortium", d: "Define roles, responsibilities, work packages and partner contributions in one workspace." },
        { t: "Bid together", d: "Coordinate documents, compliance requirements, deadlines and proposal contributions across the consortium." },
      ],
    },
    featuresOverview: {
      eyebrow: "Features",
      title: "Three modules.",
      accent: "One workflow.",
      items: [
        { t: "Tender matching", d: "Automatically find relevant public tenders and rank them by fit.", href: "#tenders" },
        { t: "AI business partner matching", d: "Find complementary companies that meet the exact tender criteria.", href: "#partners" },
        { t: "Consortium workspace", d: "Manage roles, documents, deadlines and the bid together in one place.", href: "#workspace" },
      ],
      more: "Learn more",
    },
    benefits: {
      eyebrow: "Key Benefits",
      title: "Why Procurmo",
      accent: "makes the difference.",
      items: [
        { t: "Bid for opportunities you could not pursue alone", d: "Access larger and more complex tenders by combining complementary capabilities." },
        { t: "Find partners based on requirements — not only your network", d: "Move beyond “Do I know someone?” towards structured partner matching." },
        { t: "Build consortia faster", d: "Reduce the time spent searching, contacting and evaluating potential partners." },
        { t: "Keep everyone aligned", d: "One workspace for responsibilities, documents, requirements, deadlines and contributions." },
        { t: "Reduce consortium risk", d: "Identify missing qualifications, unclear responsibilities and compliance gaps before submission." },
      ],
    },
    differentiation: {
      eyebrow: "Differentiation",
      title: "Other tools help you find tenders or write bids.",
      accent: "Procurmo helps you build the team that can actually deliver them.",
      rows: [
        { k: "Tender platforms", v: "organise opportunities." },
        { k: "AI bid tools", v: "organise content." },
        { k: "Procurmo", v: "organises companies, capabilities and collaboration." },
      ],
      pillars: ["Tender intelligence", "Partner matching", "Consortium management", "Collaborative bidding"],
      combine: "We combine tender intelligence, partner matching, consortium management and collaborative bidding in one workflow.",
      closer: "Because in complex public procurement, the strongest bid often starts with the right consortium.",
    },
    form: {
      eyebrow: "Early Access",
      title: "Get in early.",
      subline: "Register now! We'll get in touch when we're ready.",
      name: "Name", namePh: "First and last name",
      email: "Email", emailPh: "name@company.com",
      company: "Company", companyPh: "Your company",
      companyType: "Company type", companyTypePh: "Select",
      q1: "How often do you bid on public tenders?", q1Ph: "Select",
      q2: "What's the bottleneck today?", q2Ph: "Select",
      submit: "Join waitlist", submitting: "Sending …",
      reassurance: "No spam. Unsubscribe anytime.",
      successTitle: "You're on the list.",
      successBody: "Thanks — we'll get in touch when we're ready.",
      again: "Add another signup",
      errRequired: "Please fill in required fields.",
      errEmail: "Please enter a valid email.",
      errDuplicate: "This email is already on the waitlist.",
      errGeneric: "Something went wrong. Please try again.",
    },
    companyTypes: {
      sme: "SME", consulting: "Consulting", it_services: "IT service provider",
      engineering: "Engineering & planning", energy_construction: "Energy & construction",
      agency: "Agency", public_contractor: "Public contractor", other: "Other",
    },
    frequencies: { monthly: "Monthly", quarterly: "Quarterly", rarely: "Rarely", new: "New to it" },
    bottlenecks: { tenders: "Finding tenders", partners: "Finding partners", coordination: "Coordination", documents: "Docs & versions" },
    footer: {
      tagline: "Tenders, partners and bids — in one platform.",
      product: "Product", legal: "Legal",
      imprint: "Imprint", privacy: "Privacy", contact: "Contact",
      prelaunch: "Pre-launch", rights: "All rights reserved.",
    },
  },
};

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "de";
    return window.localStorage.getItem("procurmo_lang") || "de";
  });
  useEffect(() => {
    window.localStorage.setItem("procurmo_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  const value = useMemo(
    () => ({ lang, setLang, toggle: () => setLang((p) => (p === "de" ? "en" : "de")), t: dict[lang] }),
    [lang, setLang],
  );
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
